/**
 * Runway Lightbox — lightweight image gallery lightbox
 * Uses native <dialog> element. No external dependencies.
 * Avoids private class fields for max compatibility.
 */
class RunwayLightbox extends HTMLElement {
  connectedCallback() {
    // Defer init to ensure child elements are parsed
    // (script may load before children are in the DOM)
    var self = this;
    requestAnimationFrame(function () { self._init(); });
  }

  _init() {
    this._currentIndex = 0;
    this._images = [];
    this._controller = new AbortController();

    var self = this;
    var signal = this._controller.signal;

    // Parse image data
    var dataScript = this.querySelector('[ref="imageData"]');
    if (dataScript) {
      try {
        this._images = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('RunwayLightbox: Failed to parse image data', e);
      }
    }

    // Bind trigger clicks
    this.querySelectorAll('[data-lightbox-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var index = parseInt(trigger.getAttribute('data-index') || '0', 10);
        self.open(index);
      }, { signal: signal });
    });

    // Bind navigation buttons
    var closeBtn = this.querySelector('[ref="close"]');
    var prevBtn = this.querySelector('[ref="prev"]');
    var nextBtn = this.querySelector('[ref="next"]');

    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); }, { signal: signal });
    if (prevBtn) prevBtn.addEventListener('click', function () { self.prev(); }, { signal: signal });
    if (nextBtn) nextBtn.addEventListener('click', function () { self.next(); }, { signal: signal });

    // Keyboard + dialog events
    var dialog = this.querySelector('[ref="dialog"]');
    if (dialog) {
      dialog.addEventListener('keydown', function (e) { self._handleKeyDown(e); }, { signal: signal });

      // Prevent native Escape (we handle it ourselves)
      dialog.addEventListener('cancel', function (e) {
        e.preventDefault();
        self.close();
      }, { signal: signal });

      // Close on backdrop click
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) self.close();
      }, { signal: signal });

      // Touch swipe navigation
      dialog.addEventListener('touchstart', function (e) {
        self._touchStartX = e.changedTouches[0].clientX;
        self._touchStartY = e.changedTouches[0].clientY;
      }, { signal: signal, passive: true });

      dialog.addEventListener('touchend', function (e) {
        if (self._touchStartX == null) return;
        var dx = e.changedTouches[0].clientX - self._touchStartX;
        var dy = e.changedTouches[0].clientY - self._touchStartY;
        self._touchStartX = null;
        self._touchStartY = null;

        // Only trigger if horizontal swipe is dominant and > 50px
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) { self.next(); }
          else { self.prev(); }
        }
      }, { signal: signal, passive: true });
    }
  }

  disconnectedCallback() {
    if (this._controller) this._controller.abort();
  }

  open(index) {
    this._currentIndex = index;
    this._updateImage();

    var dialog = this.querySelector('[ref="dialog"]');
    if (dialog && typeof dialog.showModal === 'function') {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
  }

  close() {
    var dialog = this.querySelector('[ref="dialog"]');
    if (dialog && typeof dialog.close === 'function') {
      dialog.close();
      document.body.style.overflow = '';
    }
  }

  next() {
    this._currentIndex = (this._currentIndex + 1) % this._images.length;
    this._updateImage();
  }

  prev() {
    this._currentIndex = (this._currentIndex - 1 + this._images.length) % this._images.length;
    this._updateImage();
  }

  _updateImage() {
    var img = this.querySelector('[ref="lightboxImage"]');
    var counter = this.querySelector('[ref="counter"]');
    var loader = this.querySelector('[ref="loader"]');
    var data = this._images[this._currentIndex];

    if (img && data) {
      // Show loader, hide image until loaded
      if (loader) loader.style.display = '';
      img.style.opacity = '0';

      img.onload = function () {
        if (loader) loader.style.display = 'none';
        img.style.opacity = '1';
      };

      img.setAttribute('src', data.src);
      img.setAttribute('alt', data.alt || '');
    }

    if (counter) {
      counter.textContent = (this._currentIndex + 1) + ' / ' + this._images.length;
    }
  }

  _handleKeyDown(e) {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.next();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.prev();
        break;
    }
  }
}

if (!customElements.get('runway-lightbox')) {
  customElements.define('runway-lightbox', RunwayLightbox);
}
